// dtgDiff.js — MINSKY-16406: DTG "change over time" diff utility.
// Rewritten per MINSKY-16404's payload-shape design review (buildSitrepPayload.js's module doc
// comment cites the review in full) — flat, self-describing `events` array replaces the old
// bucketed added/removed/moved/changed-per-checkpoint-pair shape.
//
// Given the full marking set's snapshot histories, a selected DTG, and a caller-supplied
// lookback window, this builds:
//   (a) the ordered checkpoint timestamps within that window — checkpoints are every distinct
//       snapshot dtg any marking has within the window (adapts to sparse/dense data; never
//       assumes fixed 24h buckets) — plus the selected DTG itself as the final checkpoint.
//   (b) ONE flat, chronologically-ordered `events` array spanning the whole window. Each event
//       is self-describing: `{ts, markingId, type: 'added'|'removed'|'moved'|'changed',
//       ...type-specific fields}` — reconstructing one unit's story is a single filter-by-
//       markingId over this one array, not a join across separate per-checkpoint-pair buckets
//       (the review's explicitly-called-out failure mode for nested/hierarchical LLM inputs).
//       `buildSitrepPayload.js` replaces `markingId` with a short `ref` (see its own doc comment)
//       — ref-assignment spans the units list too, so it lives one layer up from this utility.
//
// Every checkpoint resolves each marking's state via resolveTrackAtDtg's existing at-or-before-
// cursor semantics (mapPmAdapter.js) — this file adds no new snapshot-resolution logic of its
// own, only the multi-checkpoint bookkeeping + diffing across the whole marking set that
// resolveTrackAtDtg (single track, single cursor) and getTrailSnapshots (single track, raw trail,
// no diff semantics) don't provide.
//
// Moved-event movement fields (distanceKm/bearingDeg/direction) are computed via geoMath.js's
// deterministic haversine utility rather than left to the LLM — see the review's GPSBench 2026
// citation (frontier LLMs are unreliable at geometric computation). A marking's own explicit
// SIDC modifiers (Z_SPEED / Q_DIRECTION_OF_MOVEMENT), when set, are surfaced alongside — never
// instead of — the computed fields, since an analyst-entered value beats a computed one when
// both exist.

import { resolveTrackAtDtg, geometryToLatLon, getEffectiveDtgSelected } from './mapPmAdapter';
import { computeMovement } from './geoMath';
import { decodeSidc } from '../sidc/sidcDecoder';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function collectCheckpoints(markings, selectedDtg, windowStart) {
    const dtgs = new Set();
    for (const m of Array.isArray(markings) ? markings : []) {
        for (const s of Array.isArray(m?.snapshots) ? m.snapshots : []) {
            if (typeof s?.dtg !== 'number') continue;
            if (s.dtg >= windowStart && s.dtg <= selectedDtg) dtgs.add(s.dtg);
        }
    }
    dtgs.add(selectedDtg);
    return [...dtgs].sort((a, b) => a - b);
}

/** Evenly downsample to `maxCheckpoints` entries, always keeping the first and last. */
function capCheckpoints(checkpoints, maxCheckpoints) {
    if (!Number.isFinite(maxCheckpoints) || maxCheckpoints <= 0 || checkpoints.length <= maxCheckpoints) {
        return checkpoints;
    }
    if (maxCheckpoints === 1) return [checkpoints[checkpoints.length - 1]];
    const last = checkpoints.length - 1;
    const step = last / (maxCheckpoints - 1);
    const picked = new Set();
    for (let i = 0; i < maxCheckpoints; i++) {
        picked.add(Math.round(i * step));
    }
    return [...picked].sort((a, b) => a - b).map((idx) => checkpoints[idx]);
}

/**
 * Resolve every marking's state at `dtg`, keyed by trackId (falls back to markingId). Decodes
 * SIDC-bearing markings via sidcDecoder.js so `diffProps` below can diff decoded fields (status,
 * quantity, etc.) — see DIFF_PROPS — the same decoder buildSitrepPayload.js's unit list uses, so
 * a diffed value always matches what the unit entry itself would show.
 *
 * @param {number} maxAgeDays - forwarded to `resolveTrackAtDtg` as its staleness ceiling: a
 *   marking whose most recent snapshot at-or-before `dtg` is older than this many days is
 *   treated as not currently present. Without this, a marking that has ever appeared would stay
 *   "visible" at every later checkpoint forever (resolveTrackAtDtg's default, unbounded
 *   "always show nearest earlier snapshot" behavior) — making the `removed` event unreachable.
 *   Tying the ceiling to the caller's own lookback window means "removed" means exactly "gone
 *   quiet for longer than the window you're looking at", the useful sitrep-facing meaning.
 */
function resolveMarkingsAt(markings, dtg, maxAgeDays) {
    const resolved = new Map();
    for (const m of Array.isArray(markings) ? markings : []) {
        const key = m?.trackId || m?.markingId;
        if (!key) continue;
        const {snapshot, visible} = resolveTrackAtDtg(m, dtg, maxAgeDays);
        if (!visible || !snapshot) continue;
        const props = snapshot.props || {};
        const decoded = props.sidc ? decodeSidc(props.sidc, props.sidcModifiers) : null;
        resolved.set(key, {
            markingId: m.markingId,
            trackId: m.trackId || null,
            label: props.label || '',
            geometry: snapshot.geometry || null,
            props,
            decoded,
        });
    }
    return resolved;
}

function sameGeometry(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Fields worth calling out as a "changed" event in a sitrep diff, per MINSKY-16404's design
 * review DIFF_PROPS expansion table.
 *
 * - `label`, `affiliation`: original set (MINSKY-16406).
 * - `status`, `reinforcedReduced`, `uniqueDesignation`, `higherFormation`, `country`,
 *   `combatEffectiveness`, `quantity`, `operationalCondition`: the review's prioritized
 *   additions — each already decoded (or newly decoded, per sidcDecoder.js's MINSKY-16404
 *   updates) into a plain string/value by `decodeSidc`, so diffing them here is a plain
 *   value-equality check, same as `label`/`affiliation`.
 * - `operationalCondition` is decoded+diffed unconditionally rather than gated behind detecting
 *   equipment/installation symbols specifically (the review floats either option as acceptable):
 *   `decodeSidc` already returns `null` for markings with no `AL_OPERATIONAL_CONDITION` modifier
 *   set (the overwhelming majority of unit-icon markings, which are unit not equipment/
 *   installation types), and `diffProps` below only ever emits a changed-event when a field's
 *   value actually differs between two checkpoints — so a marking set with zero equipment/
 *   installation symbols contributes zero operationalCondition noise "for free", without a
 *   separate detection pass. Simpler and equally low-noise, per the review's own suggestion.
 *
 * Deliberately EXCLUDED (per the review, "already folded into the prose description, low
 * value"): `echelon`, `mobilityDetail`, `headquarters`/`taskForce`/`feintDummy` (HQ-TF-feint).
 * Also never includes raw `sidc`/`sidcModifiers` (ticket MINSKY-16407: the final payload never
 * surfaces raw SIDC data; decoding both sides for a text diff is `decodeSidc`'s job above, not
 * this list's).
 */
export const DIFF_PROPS = [
    'label', 'affiliation', 'status', 'quantity', 'reinforcedReduced', 'combatEffectiveness',
    'uniqueDesignation', 'higherFormation', 'country', 'operationalCondition',
];

// DIFF_PROPS values that come from the SIDC decoder's output rather than directly off `props`.
const DECODED_DIFF_PROPS = new Set([
    'status', 'quantity', 'reinforcedReduced', 'combatEffectiveness',
    'uniqueDesignation', 'higherFormation', 'country', 'operationalCondition',
]);

function diffPropValue(state, key) {
    if (DECODED_DIFF_PROPS.has(key)) return state?.decoded?.[key] ?? null;
    return state?.props?.[key] ?? null;
}

/**
 * One flat `changed` event per differing DIFF_PROPS field (never a single event with a nested
 * `fields` object — see this module's doc comment for why).
 */
function changedEventsForField(ts, markingId, fromState, toState) {
    const events = [];
    for (const key of DIFF_PROPS) {
        const from = diffPropValue(fromState, key);
        const to = diffPropValue(toState, key);
        if (from !== to) events.push({ts, markingId, type: 'changed', field: key, from, to});
    }
    return events;
}

/** Build a "moved" event's flat lat/lon + precomputed movement fields, plus any explicit
 * Z_SPEED/Q_DIRECTION_OF_MOVEMENT modifier surfaced alongside (see this module's doc comment). */
function movedEvent(ts, markingId, fromState, toState) {
    const from = geometryToLatLon(fromState.geometry);
    const to = geometryToLatLon(toState.geometry);
    if (!from || !to) {
        // Non-point geometry (or unresolvable) on either side — still report the move happened,
        // just without lat/lon/distance/bearing fields we can't compute.
        return {ts, markingId, type: 'moved'};
    }
    const movement = computeMovement(from.lat, from.lon, to.lat, to.lon);
    const event = {
        ts, markingId, type: 'moved',
        fromLat: from.lat, fromLon: from.lon, toLat: to.lat, toLon: to.lon,
        ...movement,
    };
    if (toState.decoded?.speed) event.speed = toState.decoded.speed;
    if (toState.decoded?.directionOfMovement) event.directionOfMovement = toState.decoded.directionOfMovement;
    return event;
}

function eventsForCheckpoint(toDtg, fromState, toState) {
    const events = [];
    const keys = new Set([...fromState.keys(), ...toState.keys()]);
    for (const markingId of keys) {
        const from = fromState.get(markingId);
        const to = toState.get(markingId);
        if (!from && to) {
            events.push({ts: toDtg, markingId: to.markingId, type: 'added'});
            continue;
        }
        if (from && !to) {
            events.push({ts: toDtg, markingId: from.markingId, type: 'removed'});
            continue;
        }
        if (!from || !to) continue;

        if (!sameGeometry(from.geometry, to.geometry)) {
            events.push(movedEvent(toDtg, to.markingId, from, to));
        }
        events.push(...changedEventsForField(toDtg, to.markingId, from, to));
    }
    return events;
}

/**
 * @param {Array<{markingId: string, trackId?: string|null, snapshots?: Array<{dtg:number, geometry:object|null, props?:object}>|null}>} markings
 * @param {number|null} selectedDtg - epoch ms; the sitrep's current DTG cursor. `null`/
 *   `undefined` resolves via `getEffectiveDtgSelected` to the latest snapshot dtg across
 *   `markings` (mirrors resolveTrackAtDtg's own "no cursor" handling elsewhere), falling back to
 *   `Date.now()` only when `markings` has no snapshot history at all yet (empty-doc edge case).
 * @param {object} [opts]
 * @param {number} [opts.windowDays=7] - trailing lookback window size in days, ending at
 *   `selectedDtg`. Caller-supplied (e.g. the sitrep button's lookback-window control) — never
 *   hardcoded by this utility.
 * @param {number|null} [opts.maxCheckpoints] - optional cap on the number of checkpoints
 *   returned; when the window contains more distinct snapshot timestamps than this, they're
 *   evenly downsampled (always keeping the earliest-in-window and the selected DTG). `null`/
 *   omitted means no cap.
 * @returns {{
 *   checkpoints: number[],
 *   events: Array<{ts:number, markingId:string, type:'added'|'removed'|'moved'|'changed', [key:string]: *}>,
 * }}
 */
export function buildDtgChangeHistory(markings, selectedDtg, opts = {}) {
    const {windowDays = 7, maxCheckpoints = null} = opts || {};
    const cursor = getEffectiveDtgSelected({markings}, selectedDtg) ?? Date.now();
    const windowStart = cursor - Math.max(windowDays, 0) * MS_PER_DAY;

    const rawCheckpoints = collectCheckpoints(markings, cursor, windowStart);
    const checkpoints = capCheckpoints(rawCheckpoints, maxCheckpoints);

    // Diffing walks from `windowStart` (the trailing edge of the lookback window, not itself a
    // checkpoint) through each checkpoint in turn, so a marking born mid-window still shows up
    // as an "added" event on its very first checkpoint rather than being silently absent. When
    // there's no snapshot history inside the window at all (checkpoints is just the selected DTG
    // on its own), nothing could have changed within the window by definition — skip the
    // synthetic windowStart comparison and return no events.
    const events = [];
    if (checkpoints.length > 1) {
        let fromState = resolveMarkingsAt(markings, windowStart, windowDays);
        for (const toDtg of checkpoints) {
            const toState = resolveMarkingsAt(markings, toDtg, windowDays);
            events.push(...eventsForCheckpoint(toDtg, fromState, toState));
            fromState = toState;
        }
    }
    events.sort((a, b) => a.ts - b.ts);

    return {checkpoints, events};
}
